import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { platformAPI } from '../../../services/platformAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import { dropdownRoot } from './settingsPaths';
import '../tender/CustomerMaster.scss';
import '../tender/PersonnelPage.scss';

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const DropdownValueFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { catalog, valueId } = useParams();
  const user = useSelector((state) => state.auth.user);
  const root = dropdownRoot(location.pathname);
  const isNew = !valueId;
  const isEdit = location.pathname.endsWith('/edit');
  const readOnly = !isNew && !isEdit;
  const [form, setForm] = useState({ label: '', code: '', sortOrder: '', status: 'active', description: '', applicableAreas: [], category: '', basis: '', outcomes: [] });
  const [meta, setMeta] = useState(null);
  const [title, setTitle] = useState('Value');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const revision = useMasterRevision({ entity: 'dropdown', catalog });

  useDocumentTitle(isNew ? 'New value' : 'Dropdown value');

  useEffect(() => {
    let cancelled = false;
    platformAPI.listDropdownValues(catalog)
      .then((response) => {
        if (!cancelled) {
          setTitle(response.data?.data?.catalog?.title || 'Value');
          setMeta(response.data?.data?.catalog || null);
        }
      })
      .catch(() => {});
    if (!valueId) return undefined;
    platformAPI.getDropdownValue(catalog, valueId)
      .then((response) => {
        if (cancelled) return;
        const value = response.data?.data?.value;
        if (!value) {
          setError('That value was not found.');
          return;
        }
        setForm({
          label: value.label || '',
          code: value.code || '',
          sortOrder: value.sortOrder ?? '',
          status: value.status || 'active',
          description: value.description || '',
          applicableAreas: value.applicableAreas || [],
          category: value.meta?.category || '',
          basis: value.meta?.basis || '',
          outcomes: value.meta?.outcomes || [],
          revision: value.revision || 1
        });
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to open this value.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [catalog, valueId]);

  const setField = (name) => (event) => setForm((current) => ({ ...current, [name]: event.target.value }));

  const save = async (event) => {
    event.preventDefault();
    if (readOnly) return;
    setSaving(true);
    setError('');
    const body = {
      label: form.label,
      code: form.code,
      status: form.status,
      sortOrder: form.sortOrder === '' ? undefined : Number(form.sortOrder),
      description: form.description,
      applicableAreas: form.applicableAreas,
      category: form.category,
      basis: form.basis,
      outcomes: form.outcomes
    };
    try {
      if (!isNew) {
        const changeReason = await revision.askReason(form.revision);
        if (!changeReason) {
          setSaving(false);
          return;
        }
        body.changeReason = changeReason;
      }
      if (isNew) {
        const response = await platformAPI.createDropdownValue(catalog, body);
        const id = response.data?.data?.value?._id;
        navigate(id ? `${root}/${catalog}/${id}` : `${root}/${catalog}`, { replace: true });
      } else {
        await platformAPI.updateDropdownValue(catalog, valueId, body);
        navigate(`${root}/${catalog}`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save this value.');
      setSaving(false);
    }
  };

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Alert variant="warning">A tender administrator maintains dropdown lists.</Alert>;
  }

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <form className="cust-record pers-record" onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate(`${root}/${catalog}`)}>
              <BiArrowBack />
              {title}
            </button>
            <div className="cust-mast__row">
              <span className="cust-mark" aria-hidden>{(form.label || 'New').slice(0, 1).toUpperCase()}</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">{title}</p>
                <h1>{isNew ? 'New value' : form.label || 'Value'}</h1>
                <div className="cust-mast__meta">
                  <span className={`cust-status cust-status--${form.status === 'inactive' ? 'inactive' : 'active'}`}>
                    {form.status === 'inactive' ? 'Inactive' : 'Active'}
                  </span>
                  {form.code ? <span className="cust-chip">{form.code}</span> : null}
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            {readOnly ? (
              <Button size="sm" variant="primary" type="button" onClick={() => navigate(`${root}/${catalog}/${valueId}/edit`)}>
                Edit
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate(`${root}/${catalog}`)} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" type="submit" disabled={saving || !form.label.trim()}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </>
            )}
          </div>
        </header>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        <section className="cust-section">
          <h2><span>01</span> Value</h2>
          <div className="cust-grid">
            <Field label="Label" wide={6}>
              <Form.Control value={form.label} onChange={setField('label')} disabled={readOnly} placeholder="Under negotiation" required />
            </Field>
            <Field label="Code" wide={6}>
              <Form.Control value={form.code} onChange={setField('code')} disabled={readOnly} placeholder="Filled from the label when left blank" />
            </Field>
            <Field label="Order" wide={6}>
              <Form.Control value={form.sortOrder} onChange={setField('sortOrder')} disabled={readOnly} inputMode="numeric" placeholder="10" />
            </Field>
            <Field label="Status" wide={6}>
              <Form.Select value={form.status} onChange={setField('status')} disabled={readOnly}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Field>
            {(meta?.fields || []).includes('description') ? (
              <Field label="Description" wide={12}>
                <Form.Control as="textarea" rows={2} value={form.description} onChange={setField('description')} disabled={readOnly} />
              </Field>
            ) : null}
            {(meta?.fields || []).includes('applicableAreas') ? (
              <Field label="Applicable areas" wide={12}>
                <Form.Select multiple value={form.applicableAreas} disabled={readOnly} onChange={(event) => setForm((current) => ({ ...current, applicableAreas: Array.from(event.target.selectedOptions, (option) => option.value) }))}>
                  {(meta.areaOptions || []).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </Form.Select>
                <div className="form-text">Leave none selected when the type can be used in every area. A type does not grant access and does not make a file eligible for AI.</div>
              </Field>
            ) : null}
            {(meta?.fields || []).includes('category') ? (
              <Field label="Category" wide={6}>
                <Form.Select value={form.category} onChange={setField('category')} disabled={readOnly}>
                  <option value="">Select</option>
                  {(meta.categories || []).map((value) => <option key={value} value={value}>{value}</option>)}
                </Form.Select>
              </Field>
            ) : null}
            {(meta?.fields || []).includes('basis') ? (
              <Field label="Calculation basis" wide={6}>
                <Form.Select value={form.basis} onChange={setField('basis')} disabled={readOnly}>
                  <option value="">Select</option>
                  {(meta.bases || []).map((value) => <option key={value} value={value}>{value.replace(/_/g, ' ')}</option>)}
                </Form.Select>
              </Field>
            ) : null}
            {(meta?.fields || []).includes('outcomes') ? (
              <Field label="Applicable outcomes" wide={12}>
                <Form.Select multiple value={form.outcomes} disabled={readOnly} onChange={(event) => setForm((current) => ({ ...current, outcomes: Array.from(event.target.selectedOptions, (option) => option.value) }))}>
                  {(meta.outcomeOptions || []).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </Form.Select>
              </Field>
            ) : null}
          </div>
        </section>
      </form>
      {revision.modals}
    </div>
  );
};

export default DropdownValueFormPage;
